import DocumentMeta from 'react-document-meta';
import './tokens.css';
import styles from './login.module.css';
import React, { useState } from 'react';
import Header from '../header/index';
import { FaEye } from "react-icons/fa";
import ErrorMessage from '../../package/error/index';
import DotLoading from './loading';
import { createUserWithEmailAndPassword, getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp, getApps, initializeApp } from 'firebase/app';
import { useNavigate } from 'react-router-dom';
import { doc, getFirestore, setDoc } from 'firebase/firestore';
import { CompanyData } from 'src/type';
import AppPreview from './items/appPreview';

// Firebase 初期化はコンポーネント外で
// この静的importはESモジュールの評価順の都合で src/index.js より先に実行され得るため、
// src/index.js と同じ条件分岐(E2E時はEmulator用の固定config)にして、
// どちらが先に initializeApp を呼んでも同一のconfigになるようにしている。
// (config不一致だと2回目の呼び出しで "app/duplicate-app" 例外が発生するため)
const useEmulator = process.env.REACT_APP_USE_EMULATOR === 'true';
const firebaseConfig = useEmulator
  ? {
      apiKey: 'demo-api-key',
      authDomain: 'localhost',
      projectId: 'demo-memorii-e2e',
      storageBucket: 'demo-memorii-e2e.appspot.com',
    }
  : {
      apiKey: process.env.REACT_APP_APIKEY,
      authDomain: process.env.REACT_APP_AUTHDOMAIN,
      databaseURL: process.env.REACT_APP_DATABASEURL,
      projectId: process.env.REACT_APP_PROJECT_ID,
      storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
      messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
      appId: process.env.REACT_APP_APP_ID,
      measurementId: process.env.REACT_APP_MEASUREMENT_ID,
    };

const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

type LoadingState = "idle" | "loading" | "success" | "error";

const AuthSignIn: React.FC = () => {
  const auth = getAuth();
  const functions = getFunctions(app, "asia-northeast1");
  const navigate = useNavigate();

  const [ loading, setLoading ] = useState<LoadingState>("idle");
  const [ errorMessage, setErrorMessage ] = useState('');
  const [ worksId, setWorksId ] = useState('');
  const [ email, setEmail ] = useState('');
  const [ password, setPassword ] = useState('');
  const [ passwordEye, setPasswordEye ] = useState(false);

  const check = async () => {
    setErrorMessage('');

    if (worksId === '') {
      setErrorMessage('企業名を入力してください。');
    } else if (email.length <= 7) {
      setErrorMessage('メールアドレスを入力してください。');
    } else if (password.length <= 7) {
      setErrorMessage('パスワードを8文字以上入力してください。');
    } else {
      Register();
    }
  }

  const Register = async () => {
    setLoading("loading");

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      await saveUserData(user.uid);
      const sendVerificationEmail = httpsCallable(functions, "sendSignupVerificationEmail");
      await sendVerificationEmail({ email: user.email });

      setLoading("success");
    } catch (error: any) {
      setLoading("error");

      switch (error.code) {
        case "auth/email-already-in-use":
          setErrorMessage("入力したメールアドレスは既に登録されています。");
          break;
        case "auth/invalid-email":
          setErrorMessage("入力したメールは無効なメールアドレス形式です。");
          break;
        case "auth/weak-password":
          setErrorMessage("パスワードが弱すぎます。より強力なパスワードを設定してください。");
          break;
        default:
          setErrorMessage("予期しないエラーが発生しました。時間をおいて再度お試しください。");
          break;
      }
    }
  };

  const saveUserData = async (uid: string) => {
    const db = getFirestore();
    const docRef = doc(db, "companies", uid);

    const dataToSend: CompanyData = {
      member_id: worksId,
      id: uid,
      name: worksId,
      logo: false,
      owner: [ uid ],
      invite: [ email ],
      judgeUids: [],
      judgeInviteEmails: [],
      judgeInvites: [],
      time: new Date(),
      contract: false,
      notification: {
        mail: email,
        lineId: "",
        linePicture: "",
        lineName: "",
      },
      progress: {
        plan: false,
        icon: false,
        jobPostings: false,
        sending: false,
        complete: false,
      },
      role: 'owner',
      judgeName: '',
    };
    await setDoc(docRef, dataToSend);
  }

  const meta = {
    title: "新規登録 | 株式会社memorii（memorii works）",
    description: "株式会社memoriiが提供する『memorii works』の新規登録ページです。美容室の方はこちらからアカウント登録ができます。",
    canonical: "https://memorii-works.com/company/signin",
    meta: { charset: "utf-8", name: { robots: 'noindex,nofollow,noarchive,noimageindex' } }
  };

  return (
    <DocumentMeta {...meta}>
      <div className={styles.page}>

        <Header />

        <main className={styles.wrap}>
          <div className={styles.card}>

            <section className={styles.brand}>
              <div className={styles.brandInner}>
                <p className={styles.brandTagline}>
                  美容業界の新卒採用を、<br />
                  ひとつの仕組みに。
                </p>
                <AppPreview />
              </div>
            </section>

            <section className={styles.panel}>

              {loading === "loading" && (
                <div className={styles.state}>
                  <DotLoading text="処理中" />
                </div>
              )}

              {loading === "success" && (
                <div className={styles.state}>
                  <p className={styles.stateText}>
                    新規登録が完了しましたので、届いたメールのリンクをクリックし、本登録を完了してください。
                  </p>
                  <button type="button" className={styles.linkBtn} onClick={() => navigate('/company/login')}>
                    ログインはこちらから
                  </button>
                </div>
              )}

              {(loading === "idle" || loading === "error") && (
                <form
                  className={styles.form}
                  onSubmit={(e) => { e.preventDefault(); check(); }}
                >
                  <h1 className={styles.title}>新規登録</h1>

                  {errorMessage && <ErrorMessage message={errorMessage} />}

                  <div className={styles.field}>
                    <label htmlFor="signin-works" className={styles.label}>企業名</label>
                    <input
                      id="signin-works"
                      className={styles.input}
                      placeholder="memorii"
                      value={worksId}
                      onChange={(e) => setWorksId(e.target.value)}
                    />
                  </div>

                  <div className={styles.field}>
                    <label htmlFor="signin-email" className={styles.label}>メールアドレス</label>
                    <input
                      id="signin-email"
                      className={styles.input}
                      type="email"
                      autoComplete="email"
                      placeholder="memorii@trankllc.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>

                  <div className={styles.field}>
                    <label htmlFor="signin-password" className={styles.label}>パスワード</label>
                    <div className={styles.inputWrap}>
                      <input
                        id="signin-password"
                        className={styles.input}
                        type={passwordEye ? 'text' : 'password'}
                        autoComplete="new-password"
                        placeholder="8文字以上"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      <button
                        type="button"
                        className={styles.eye}
                        aria-label={passwordEye ? 'パスワードを隠す' : 'パスワードを表示'}
                        aria-pressed={passwordEye}
                        onClick={() => setPasswordEye(!passwordEye)}
                      >
                        <FaEye />
                      </button>
                    </div>
                  </div>

                  <button type="submit" className={styles.submit}>新規登録する</button>

                  <div className={styles.links}>
                    <button type="button" className={styles.linkBtn} onClick={() => navigate('/company/login')}>
                      アカウントをお持ちの方はこちら
                    </button>
                  </div>
                </form>
              )}

            </section>

          </div>
        </main>

      </div>
    </DocumentMeta>
  );
}

export default AuthSignIn;
